/*
 * project-gallery.js — First Light Astro
 * Enhances the CSS scroll-snap "project-gallery" strip:
 *   (A) pointer drag-to-scroll + prev/next arrows (smooth). Native touch swipe
 *       and native HORIZONTAL wheel/trackpad keep working. Vertical wheel is
 *       NEVER hijacked, so normal page scrolling never moves the slider.
 *   (B) a Fancybox-like accessible lightbox (focus trap, keyboard, swipe,
 *       counter, robust body scroll-lock).
 *
 * Leak-free & theme-editor-safe: one IIFE; each root self-inits once
 * (root.dataset.pgsReady); a per-root AbortController owns ALL listeners + the
 * injected lightbox; global shopify:section lifecycle bound once; no perpetual rAF.
 */
(function () {
  'use strict';

  /* ----------------------------------------- shared, robust body scroll-lock */
  // position:fixed technique — disables background scroll on desktop AND iOS,
  // and preserves/restores the scroll position. Only one lightbox open at a time.
  var scrollLocked = false;
  var savedScrollY = 0;
  function lockScroll() {
    if (scrollLocked) return;
    savedScrollY = window.scrollY || window.pageYOffset || 0;
    var b = document.body;
    b.style.position = 'fixed';
    b.style.top = -savedScrollY + 'px';
    b.style.left = '0';
    b.style.right = '0';
    b.style.width = '100%';
    scrollLocked = true;
  }
  function unlockScroll() {
    if (!scrollLocked) return;
    var b = document.body;
    b.style.position = '';
    b.style.top = '';
    b.style.left = '';
    b.style.right = '';
    b.style.width = '';
    scrollLocked = false;
    window.scrollTo(0, savedScrollY);
  }

  /* ============================================================= LIGHTBOX */
  function buildLightbox(root, signal) {
    var doc = root.ownerDocument;

    var overlay = doc.createElement('div');
    overlay.className = 'pgs-lb';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', 'Image gallery');
    overlay.hidden = true;
    overlay._pgsRoot = root; // link for orphan cleanup on re-render

    overlay.innerHTML =
      '<div class="pgs-lb__backdrop" data-pgs-close></div>' +
      '<button type="button" class="pgs-lb__btn pgs-lb__close" data-pgs-close aria-label="Close (Esc)">' +
      '  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6L6 18"/></svg>' +
      '</button>' +
      '<button type="button" class="pgs-lb__btn pgs-lb__nav pgs-lb__prev" data-pgs-prev aria-label="Previous image">' +
      '  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M15 5l-7 7 7 7"/></svg>' +
      '</button>' +
      '<button type="button" class="pgs-lb__btn pgs-lb__nav pgs-lb__next" data-pgs-next aria-label="Next image">' +
      '  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M9 5l7 7-7 7"/></svg>' +
      '</button>' +
      '<div class="pgs-lb__count" data-pgs-count aria-hidden="true"></div>' +
      '<figure class="pgs-lb__figure" data-pgs-stage>' +
      '  <img class="pgs-lb__img" alt="" />' +
      '  <figcaption class="pgs-lb__caption" data-pgs-caption></figcaption>' +
      '</figure>';

    doc.body.appendChild(overlay);

    var img = overlay.querySelector('.pgs-lb__img');
    var captionEl = overlay.querySelector('[data-pgs-caption]');
    var countEl = overlay.querySelector('[data-pgs-count]');
    var prevBtn = overlay.querySelector('[data-pgs-prev]');
    var nextBtn = overlay.querySelector('[data-pgs-next]');
    var closeBtn = overlay.querySelector('.pgs-lb__close');
    var stage = overlay.querySelector('[data-pgs-stage]');

    var state = { items: [], index: 0, open: false, lastTrigger: null, keyHandler: null };

    function render() {
      var it = state.items[state.index];
      if (!it) return;
      img.src = it.full;
      img.alt = it.alt || it.caption || '';
      if (it.caption) { captionEl.textContent = it.caption; captionEl.hidden = false; }
      else { captionEl.textContent = ''; captionEl.hidden = true; }
      var multi = state.items.length > 1;
      prevBtn.hidden = !multi;
      nextBtn.hidden = !multi;
      if (multi) { countEl.textContent = (state.index + 1) + ' / ' + state.items.length; countEl.hidden = false; }
      else { countEl.hidden = true; }
    }

    function go(delta) {
      if (state.items.length < 2) return;
      var n = state.items.length;
      state.index = (state.index + delta + n) % n;
      render();
    }

    function getFocusable() {
      return Array.prototype.filter.call(
        overlay.querySelectorAll('button:not([hidden])'),
        function (el) { return el.offsetParent !== null; }
      );
    }

    function onKeyDown(e) {
      if (!state.open) return;
      if (e.key === 'Escape') { e.preventDefault(); close(); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); go(1); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); go(-1); }
      else if (e.key === 'Tab') {
        var f = getFocusable();
        if (!f.length) { e.preventDefault(); return; }
        var first = f[0], last = f[f.length - 1];
        if (e.shiftKey && doc.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && doc.activeElement === last) { e.preventDefault(); first.focus(); }
        else if (!overlay.contains(doc.activeElement)) { e.preventDefault(); first.focus(); }
      }
    }

    function open(items, index, trigger) {
      state.items = items;
      state.index = index || 0;
      state.lastTrigger = trigger || null;
      render();
      lockScroll();
      doc.documentElement.classList.add('pgs-lb-open'); // hides slider arrows while open
      overlay.hidden = false;
      state.open = true;
      state.keyHandler = onKeyDown;
      doc.addEventListener('keydown', state.keyHandler);
      closeBtn.focus();

      // Gate the scale(0)->scale(1) zoom on the image actually having its real
      // dimensions. On the first open the large image isn't cached yet, so
      // without this the transition would run on an empty (0-sized) figure and
      // the image would just pop in. Wait for decode/load (with a safety
      // timeout), then flush the reflow and trigger the transition. Cached
      // opens resolve ~instantly, so there's no perceptible delay.
      var animated = false;
      function reveal() {
        if (animated || !state.open) return;
        animated = true;
        void overlay.offsetWidth; // reflow so the transition runs
        overlay.classList.add('is-open');
      }
      if (img.decode) {
        img.decode().then(reveal).catch(reveal);
      } else if (!img.complete) {
        img.addEventListener('load', reveal, { once: true, signal: signal });
        img.addEventListener('error', reveal, { once: true, signal: signal });
      } else {
        reveal();
      }
      setTimeout(reveal, 400); // never leave the figure stuck at scale(0)
    }

    function close() {
      if (!state.open) return;
      state.open = false;
      overlay.classList.remove('is-open');
      unlockScroll();
      doc.documentElement.classList.remove('pgs-lb-open');
      if (state.keyHandler) { doc.removeEventListener('keydown', state.keyHandler); state.keyHandler = null; }
      var trigger = state.lastTrigger;
      // Defer hiding AND clearing the src until the close transition finishes,
      // so the figure stays full-sized while it animates scale(1)->scale(0)
      // (the zoom-out). Clearing src earlier would empty the figure to zero
      // size and the scale-down would have nothing visible to animate.
      var hidden = false;
      var hide = function () {
        if (hidden || state.open) return;
        hidden = true;
        overlay.hidden = true;
        img.removeAttribute('src');
        overlay.removeEventListener('transitionend', hide);
      };
      // .pgs-lb__figure transition is .3s; wait for the transform to end on it
      // specifically so opacity (.25s) finishing first doesn't cut it short.
      overlay.addEventListener('transitionend', function (e) {
        if (e.target === stage && e.propertyName === 'transform') hide();
      }, { signal: signal });
      setTimeout(hide, 350); // fallback if no transitionend fires
      if (trigger && typeof trigger.focus === 'function') trigger.focus();
    }

    // controls (delegated; all auto-removed via signal on teardown)
    overlay.addEventListener('click', function (e) {
      if (e.target.closest('[data-pgs-prev]')) { e.preventDefault(); go(-1); return; }
      if (e.target.closest('[data-pgs-next]')) { e.preventDefault(); go(1); return; }
      // Clicking the active image advances to the next image (forward-only).
      // go(1) safely no-ops when there is only a single image.
      if (e.target.closest('.pgs-lb__img')) { e.preventDefault(); go(1); return; }
      // Close on the X, the backdrop, OR any empty overlay space —
      // i.e. any click that is NOT on the image/caption (the figure stage).
      if (e.target.closest('[data-pgs-close]') || !e.target.closest('[data-pgs-stage]')) {
        e.preventDefault();
        close();
      }
    }, { signal: signal });

    // swipe to navigate inside the lightbox
    var sx = 0, sy = 0, swiping = false;
    stage.addEventListener('touchstart', function (e) {
      if (e.touches.length !== 1) return;
      swiping = true; sx = e.touches[0].clientX; sy = e.touches[0].clientY;
    }, { passive: true, signal: signal });
    stage.addEventListener('touchend', function (e) {
      if (!swiping) return; swiping = false;
      var t = e.changedTouches[0], dx = t.clientX - sx, dy = t.clientY - sy;
      if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) go(dx < 0 ? 1 : -1);
    }, { passive: true, signal: signal });

    return { open: open, close: close, isOpen: function () { return state.open; }, el: overlay };
  }

  /* =============================================================== SLIDER */
  function initSlider(root, slider, signal) {
    /* ---- pointer drag (mouse/pen only; touch uses native scrolling) ----
       Carousel-style: the live drag is clamped to a single image in either
       direction, and on release the track settles smoothly onto exactly one
       slide in the drag direction — never a multi-slide jump. Native touch
       swipe is intentionally left on native scrolling. */
    var down = false, moved = false, startX = 0, startScroll = 0, pointerId = null;
    var dragDx = 0, startIndex = 0, itemW = 0, maxScroll = 0, animFrame = null;
    var DRAG_THRESHOLD = 6;

    function itemWidth() {
      var first = slider.querySelector('.pg__item');
      return first ? first.getBoundingClientRect().width : 0;
    }

    // Smooth settle via rAF easing. Native scrollTo({behavior:'smooth'}) stutters
    // here because CSS scroll-snap re-engages and fights it; we suppress snap for
    // the duration of the tween and restore it when the glide finishes.
    function cancelAnim() {
      // NOTE: only cancel the frame here — must NOT reset scroll-snap-type,
      // because re-enabling snap while scrollLeft is mid-slide makes the browser
      // instantly snap to the nearest slide, which both skips the tween and
      // reads as a glitchy jump.
      if (animFrame) { cancelAnimationFrame(animFrame); animFrame = null; }
    }
    function animateTo(targetLeft) {
      var startLeft = slider.scrollLeft;           // read BEFORE any snap toggle
      cancelAnim();
      var dist = targetLeft - startLeft;
      if (Math.abs(dist) < 1) { slider.style.scrollSnapType = ''; return; }
      var duration = 320, startTs = null;
      slider.style.scrollSnapType = 'none';        // don't let snap fight the tween
      function easeOutCubic(t) { return 1 - Math.pow(1 - t, 3); }
      function step(ts) {
        if (startTs === null) startTs = ts;
        var p = Math.min((ts - startTs) / duration, 1);
        slider.scrollLeft = startLeft + dist * easeOutCubic(p);
        if (p < 1) { animFrame = requestAnimationFrame(step); }
        else { animFrame = null; slider.style.scrollSnapType = ''; }
      }
      animFrame = requestAnimationFrame(step);
    }

    slider.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'touch') return;          // let native touch scroll
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      cancelAnim();                                   // stop any in-flight settle
      autoDefer();                                    // a manual grab overrides auto-scroll
      slider.style.scrollSnapType = '';               // back to resting CSS snap (we're on a snap point)
      down = true; moved = false; dragDx = 0;
      startX = e.clientX; startScroll = slider.scrollLeft; pointerId = e.pointerId;
      itemW = itemWidth();
      maxScroll = slider.scrollWidth - slider.clientWidth;
      startIndex = itemW ? Math.round(startScroll / itemW) : 0;
    }, { signal: signal });

    slider.addEventListener('pointermove', function (e) {
      if (!down || e.pointerId !== pointerId) return;
      var dx = e.clientX - startX;
      if (!moved && Math.abs(dx) > DRAG_THRESHOLD) {
        moved = true;
        slider.classList.add('pg--dragging');
        try { slider.setPointerCapture(pointerId); } catch (err) {}
      }
      if (moved) {
        e.preventDefault();
        dragDx = dx;
        // Free 1:1 follow — the track tracks the cursor exactly (no hard clamp,
        // so it never "sticks"). The one-slide limit is enforced on release.
        slider.scrollLeft = startScroll - dx;
      }
    }, { signal: signal });

    function settleDrag() {
      if (!itemW) return;
      // Settle to the nearest slide, but never more than one image away from
      // where the drag began — guarantees one image per gesture, no multi-jump.
      var index = Math.round(slider.scrollLeft / itemW);
      if (index > startIndex + 1) index = startIndex + 1;
      if (index < startIndex - 1) index = startIndex - 1;
      var maxIndex = Math.round(maxScroll / itemW);
      if (index < 0) index = 0;
      if (index > maxIndex) index = maxIndex;
      animateTo(index * itemW);
    }

    function endDrag(e) {
      if (!down) return;
      if (e && pointerId !== null && e.pointerId !== pointerId) return;
      down = false;
      // Keep snap suppressed continuously into the settle so proximity-snap
      // can't fire a one-frame jump in the gap before the tween starts.
      if (moved) slider.style.scrollSnapType = 'none';
      slider.classList.remove('pg--dragging');
      if (pointerId !== null) { try { slider.releasePointerCapture(pointerId); } catch (err) {} }
      pointerId = null;
      if (moved) settleDrag();                     // glide to exactly one slide (or back)
    }
    slider.addEventListener('pointerup', endDrag, { signal: signal });
    slider.addEventListener('pointercancel', endDrag, { signal: signal });
    slider.addEventListener('lostpointercapture', endDrag, { signal: signal });

    // Stop the browser's native image/element drag (the "ghost image" drag) from
    // hijacking the pointer gesture — without this the carousel drag never starts.
    slider.addEventListener('dragstart', function (e) { e.preventDefault(); }, { signal: signal });

    // suppress the click that follows a real drag (capture) so it won't open the lightbox
    slider.addEventListener('click', function (e) {
      if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; }
    }, { capture: true, signal: signal });

    /* ---- prev/next arrows (smooth, by ~one viewport) ---- */
    var arrows = root.querySelectorAll('[data-pgs-arrow]');
    function updateArrows() {
      var max = slider.scrollWidth - slider.clientWidth;
      Array.prototype.forEach.call(arrows, function (btn) {
        var next = btn.getAttribute('data-pgs-arrow') === 'next';
        var atEdge = next ? slider.scrollLeft >= max - 1 : slider.scrollLeft <= 0;
        btn.disabled = (max <= 1) || atEdge;
      });
    }
    Array.prototype.forEach.call(arrows, function (btn) {
      btn.addEventListener('click', function () {
        var dir = btn.getAttribute('data-pgs-arrow') === 'next' ? 1 : -1;
        var amount = Math.max(Math.round(slider.clientWidth * 0.8), 240);
        slider.scrollBy({ left: dir * amount, behavior: 'smooth' });
        autoDefer();                              // manual nav resets the auto countdown
      }, { signal: signal });
    });
    slider.addEventListener('scroll', updateArrows, { passive: true, signal: signal });
    updateArrows();

    // NOTE: no wheel handler — vertical wheel scrolls the page (no auto-jump);
    // native overflow-x handles horizontal wheel / trackpad gestures.

    /* ---- optional auto-scroll (one image per tick; pauses on interaction) ----
       Reuses animateTo so each transition matches manual navigation. Advances a
       single image and wraps back to the first at the end. Never overrides a
       manual action — it simply skips ticks while the user is interacting. */
    var autoTimer = null;
    var autoEnabled = root.getAttribute('data-pgs-autoplay') === '1';
    var autoInterval = parseInt(root.getAttribute('data-pgs-interval'), 10);
    if (!autoInterval || autoInterval < 1000) autoInterval = 4000;
    var autoHover = false;

    function lbOpen() {
      return root.ownerDocument.documentElement.classList.contains('pgs-lb-open');
    }
    function autoAdvance() {
      var w = itemWidth();
      if (!w) return;
      var max = slider.scrollWidth - slider.clientWidth;
      if (max <= 1) return;                        // nothing to scroll
      var maxIndex = Math.round(max / w);
      var next = Math.round(slider.scrollLeft / w) + 1;
      if (next > maxIndex) next = 0;               // wrap back to the first image
      animateTo(next * w);
    }
    function autoTick() {
      // Skip (don't advance) while dragging, hovering, in the lightbox, or on a
      // hidden tab — so auto-scroll never fights a manual interaction.
      if (down || autoHover || lbOpen() || document.hidden) return;
      autoAdvance();
    }
    function autoStart() { if (autoEnabled && !autoTimer) autoTimer = setInterval(autoTick, autoInterval); }
    function autoStop() { if (autoTimer) { clearInterval(autoTimer); autoTimer = null; } }
    function autoDefer() { if (autoEnabled) { autoStop(); autoStart(); } } // restart countdown after manual action

    if (autoEnabled) {
      // Pause while the pointer is over the carousel so it never slides out from
      // under the user; resume when the pointer leaves.
      slider.addEventListener('mouseenter', function () { autoHover = true; }, { signal: signal });
      slider.addEventListener('mouseleave', function () { autoHover = false; }, { signal: signal });
      autoStart();
    }

    return { destroy: function () { cancelAnim(); autoStop(); } };
  }

  /* ================================================================= INIT */
  function collectItems(root) {
    var triggers = root.querySelectorAll('[data-pgs-full]');
    var items = [];
    Array.prototype.forEach.call(triggers, function (el, i) {
      el._pgsIndex = i;
      items.push({
        full: el.getAttribute('data-pgs-full'),
        caption: el.getAttribute('data-pgs-caption') || '',
        alt: el.getAttribute('data-pgs-alt') || el.getAttribute('data-pgs-caption') || ''
      });
    });
    return items;
  }

  function initRoot(root) {
    if (!root || root.dataset.pgsReady === '1') return;
    var slider = root.querySelector('.pg__slider');
    // A root may be a full slider+lightbox gallery, OR a lightbox-only root (e.g.
    // Project Rich text) that just has clickable [data-pgs-full] images. Bail
    // only when there is neither a slider nor any lightbox triggers.
    var hasTriggers = !!root.querySelector('[data-pgs-full]');
    if (!slider && !hasTriggers) { root.dataset.pgsReady = '1'; return; }
    root.dataset.pgsReady = '1';

    var controller = new AbortController();
    var signal = controller.signal;
    root._pgsController = controller;

    var sliderApi = slider ? initSlider(root, slider, signal) : null;
    var lb = buildLightbox(root, signal);

    root._pgsTeardown = function () {
      controller.abort();
      if (sliderApi) sliderApi.destroy();
      if (lb && lb.isOpen()) lb.close();
      if (lb && lb.el && lb.el.parentNode) lb.el.parentNode.removeChild(lb.el);
      if (scrollLocked) unlockScroll(); // never leave the body locked
    };

    function openFrom(trigger) {
      lb.open(collectItems(root), trigger._pgsIndex || 0, trigger);
    }
    // Delegate from the slider when present (so drag-click suppression applies);
    // otherwise delegate from the root for lightbox-only image triggers.
    var clickTarget = slider || root;
    clickTarget.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-pgs-full]');
      if (trigger && root.contains(trigger)) { e.preventDefault(); openFrom(trigger); }
    }, { signal: signal });
    // (buttons fire native click on Enter/Space, so no extra keydown handler needed)
  }

  function teardownRoot(root) {
    if (!root) return;
    if (typeof root._pgsTeardown === 'function') { root._pgsTeardown(); root._pgsTeardown = null; }
    root._pgsController = null;
    delete root.dataset.pgsReady;
  }

  function initAll(scope) {
    var roots = (scope || document).querySelectorAll('[data-pgs]');
    Array.prototype.forEach.call(roots, initRoot);
  }

  /* --------------------------------------------------- boot + section life */
  function ready(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn, { once: true });
    else fn();
  }
  ready(function () { initAll(document); });

  if (!window.__pgsBound) {
    window.__pgsBound = true;
    document.addEventListener('shopify:section:load', function (e) {
      // re-render fires :load without :unload — sweep orphan overlays first
      var orphans = document.querySelectorAll('.pgs-lb');
      Array.prototype.forEach.call(orphans, function (ov) {
        if (ov._pgsRoot && !document.contains(ov._pgsRoot) && ov.parentNode) ov.parentNode.removeChild(ov);
      });
      if (scrollLocked) unlockScroll();
      document.documentElement.classList.remove('pgs-lb-open');
      var sectionEl = e.target;
      var roots = sectionEl && sectionEl.querySelectorAll ? sectionEl.querySelectorAll('[data-pgs]') : [];
      Array.prototype.forEach.call(roots, initRoot);
    });
    document.addEventListener('shopify:section:unload', function (e) {
      var sectionEl = e.target;
      if (!sectionEl || !sectionEl.querySelectorAll) return;
      Array.prototype.forEach.call(sectionEl.querySelectorAll('[data-pgs]'), teardownRoot);
    });
  }
})();
